import { Suspense, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth, type LazyView } from "@/platform/client";
import { IconChevronLeft, IconChevronRight, IconLogout, IconMenu, IconX } from "@/platform/ui";
import { NotificationBell, NotificationOverlay } from "@/notifications/client";
import { WaypointLogo } from "@/platform/ui";
import { cx, UnreadBadge } from "@/platform/ui";
import { ActiveRegionProvider } from "@/platform/client";
import { useFeeds } from "@/feeds/client";
import { dashboardQueries } from "@/finance/client";
import { Clocks } from "@/shell/layout/Clocks.js";
import { RightSidebar, RightSidebarAthena } from "@/shell/layout/RightSidebar.js";
import { MOBILE_TABS, NAV } from "@/shell/layout/nav.js";
import { usePrefetch } from "@/shell/layout/usePrefetch.js";
import {
  DEFAULT_RIGHT_PANEL,
  isRightPanelKey,
  type RightPanelKey,
} from "@/shell/layout/rightPanels.js";

function SidebarContent({
  collapsed,
  onNavigate,
  feedsUnread,
  feedsAccent,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  feedsUnread: number;
  feedsAccent: boolean;
}) {
  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-2 [scrollbar-gutter:stable]">
      {NAV.map((group) => (
        <div key={group.heading}>
          {!collapsed && (
            <div className="px-2.5 pb-1.5 pt-3 text-[10px] font-bold tracking-[0.1em] text-faint">
              {group.heading.toUpperCase()}
            </div>
          )}
          {group.items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end
                onClick={onNavigate}
                title={item.label}
                className={({ isActive }) =>
                  cx(
                    "flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
                    collapsed && "justify-center",
                    isActive
                      ? "bg-accent-soft text-accent"
                      : "text-dim hover:bg-surface-2 hover:text-ink",
                  )
                }
              >
                <Icon size={17} className="flex-none" />
                {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
                {!collapsed && item.to === "/feeds" && feedsUnread > 0 && (
                  <UnreadBadge count={feedsUnread} accent={feedsAccent} />
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function UserFooter({ collapsed, onLogout }: { collapsed: boolean; onLogout: () => void }) {
  return (
    <div className="border-t border-line p-3">
      <button
        onClick={onLogout}
        className={cx(
          "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-ink",
          collapsed && "justify-center",
        )}
        title="Sign out"
      >
        <IconLogout size={17} className="flex-none" />
        {!collapsed && <span>Sign out</span>}
      </button>
    </div>
  );
}

/** The code of every page and of the chat, and Market's and Cash flow's
 *  data, loaded ahead wherever the person is. Its own component, so what it
 *  watches re-renders nothing else. */
function Preload({ pages }: { pages: readonly LazyView[] }) {
  const views = useMemo(() => [...pages, RightSidebarAthena], [pages]);
  usePrefetch(dashboardQueries, views);
  return null;
}

export function AppShell({ pages }: { pages: readonly LazyView[] }) {
  const { logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("wp:sidebar-collapsed") === "1",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(
    () => localStorage.getItem("wp:right-collapsed") === "1",
  );
  const { data: feeds = [] } = useFeeds();
  const feedsUnread = useMemo(() => feeds.reduce((s, f) => s + f.unreadCount, 0), [feeds]);
  const feedsAccent = useMemo(() => feeds.some((f) => f.hasNew), [feeds]);
  const [rightPanel, setRightPanel] = useState<RightPanelKey>(() => {
    const stored = localStorage.getItem("wp:right-panel");
    return isRightPanelKey(stored) ? stored : DEFAULT_RIGHT_PANEL;
  });

  useEffect(() => {
    localStorage.setItem("wp:sidebar-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);
  useEffect(() => {
    localStorage.setItem("wp:right-collapsed", rightCollapsed ? "1" : "0");
  }, [rightCollapsed]);
  useEffect(() => {
    localStorage.setItem("wp:right-panel", rightPanel);
  }, [rightPanel]);

  // close the mobile drawer on navigation
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  return (
    <ActiveRegionProvider>
      <div className="flex h-dvh bg-bg">
        {/* ---- desktop sidebar ---- */}
        <aside
          className={cx(
            "sticky top-0 hidden h-dvh flex-none flex-col border-r border-line bg-surface transition-[width] duration-200 motion-reduce:transition-none lg:flex",
            collapsed ? "w-[68px]" : "w-60",
          )}
        >
          <div className={cx("flex items-center px-4 py-5", collapsed && "justify-center")}>
            <WaypointLogo showText={!collapsed} markClassName="h-8 w-8 rounded-lg" />
          </div>
          <SidebarContent
            collapsed={collapsed}
            feedsUnread={feedsUnread}
            feedsAccent={feedsAccent}
          />
          <button
            onClick={() => setCollapsed((v) => !v)}
            className={cx(
              "mx-3 mb-1 flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium text-faint hover:bg-surface-2 hover:text-ink",
              collapsed && "justify-center",
            )}
          >
            {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
            {!collapsed && "Collapse"}
          </button>
          <UserFooter collapsed={collapsed} onLogout={logout} />
        </aside>

        {/* ---- mobile drawer ---- */}
        {drawerOpen && (
          <div
            className="fixed inset-x-0 top-0 z-40 h-dvh lg:hidden"
            onClick={() => setDrawerOpen(false)}
          >
            <div className="absolute inset-0 bg-black/60" />
            <aside
              className="absolute left-0 top-0 flex h-full w-64 flex-col border-r border-line bg-surface"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-5">
                <WaypointLogo showText markClassName="h-8 w-8 rounded-lg" />
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="rounded-md p-1 text-muted hover:text-ink"
                >
                  <IconX size={18} />
                </button>
              </div>
              <SidebarContent
                collapsed={false}
                onNavigate={() => setDrawerOpen(false)}
                feedsUnread={feedsUnread}
                feedsAccent={feedsAccent}
              />
              <UserFooter collapsed={false} onLogout={logout} />
            </aside>
          </div>
        )}

        {/* ---- main column ---- */}
        {/* Its own bounded, self-scrolling column (min-h-0 + overflow-y-auto on
          a h-dvh-implied flex sibling of the two sticky/h-dvh asides) —
          without this, main content overflow makes the whole document scroll,
          producing a second, page-level scrollbar right next to the right
          sidebar's own internal one. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]">
          <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-line bg-bg/90 px-4 py-3 backdrop-blur sm:px-6">
            <button
              onClick={() => setDrawerOpen(true)}
              className="rounded-lg border border-line bg-surface p-2 text-dim hover:text-ink lg:hidden"
              aria-label="Open menu"
            >
              <IconMenu size={18} />
            </button>
            <div className="flex-1" />
            <div className="flex items-stretch gap-2 sm:gap-2.5">
              <Clocks />
              <NotificationBell />
            </div>
          </header>

          {/* `@container` makes this column a containment context, so a page can
            size its grid on the space it actually has. Viewport breakpoints lie
            here: the nav and the right rail come and go, so the content column
            is not monotonic in the window — at a 1024px viewport it is 431px
            wide, at 900px it is 891px. `lg:grid-cols-3` was therefore dividing
            431px three ways while `md` got 891px in one. See docs/design-system.md. */}
          <main className="@container flex-1 px-4 pb-24 pt-5 sm:px-6 lg:pb-8">
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </main>

          {/* ---- mobile bottom tabs ---- */}
          <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface/95 backdrop-blur lg:hidden">
            {MOBILE_TABS.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    cx(
                      "flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium",
                      isActive ? "text-accent" : "text-muted",
                    )
                  }
                >
                  <span className="relative flex">
                    <Icon size={19} />
                    {item.to === "/feeds" && feedsUnread > 0 && (
                      <UnreadBadge
                        count={feedsUnread}
                        accent={feedsAccent}
                        className="absolute -top-1.5 -right-2.5"
                      />
                    )}
                  </span>
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* ---- right sidebar: Overview (calendar/agenda/tasks) or Athena (chat) ---- */}
        <RightSidebar
          collapsed={rightCollapsed}
          onToggle={() => setRightCollapsed((v) => !v)}
          panel={rightPanel}
          onPanelChange={setRightPanel}
        />

        {/* ---- global notification overlay (top-right) ---- */}
        <NotificationOverlay />
        <Preload pages={pages} />
      </div>
    </ActiveRegionProvider>
  );
}
